// Libraries
import { TriangleDownIcon } from '@chakra-ui/icons';
import { Button } from '@chakra-ui/react';
import { KeyboardEvent, useCallback } from 'react';
// Types
import { DropdownButtonProps } from '../Dropdown.types';

/**
 * `DropdownButton` is a specialized button component designed to control and reflect the state of a dropdown.
 *
 * @component
 * @param {DropdownButtonProps} props - The properties that define the `DropdownButton` component.
 * @param {React.Ref} props.innerRef - The ref object that gets attached to the button, typically used for handling focus events.
 * @param {boolean} props.isOpen - The current state of the dropdown (open/closed).
 * @param {React.Dispatch<React.SetStateAction<boolean>>} props.setIsOpen - The function used to update the state of the dropdown.
 * @param {React.ReactNode} props.children - The contents of the `DropdownButton`, typically the button's label.
 *
 * @returns {ReactElement} Returns a button component that toggles the open/closed state of a dropdown when clicked, and displays an arrow icon to indicate the current state.
 */
export const DropdownButton = ({ innerRef, isOpen, setIsOpen, children, ...buttonProps }: DropdownButtonProps) => {
  /**
   * Callback function to handle a keydown event on an HTML button element.
   *
   * @function
   * @param event - The keyboard event. It is inferred to be of type `KeyboardEvent<HTMLButtonElement>`.
   */
  const handleKeyDown = useCallback(
    ({ key }: KeyboardEvent<HTMLButtonElement>) => {
      if (key === 'ArrowDown' && !isOpen) setIsOpen(true);
    },
    [isOpen, setIsOpen]
  );

  /**
   * Function to handle a click event, toggling the state of a dropdown or modal.
   *
   * @function
   */
  const handleOnClick = () => {
    setIsOpen((prev) => !prev);
  };

  return (
    <Button
      ref={innerRef}
      isActive={isOpen}
      rightIcon={<TriangleDownIcon boxSize={2.5} color='base' />}
      variant='dropdown'
      onClick={handleOnClick}
      onKeyDown={handleKeyDown}
      h={8}
      bg='white'
      {...buttonProps}
    >
      {children}
    </Button>
  );
};
